import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, } from '@/components/ui/dialog';
import { CalendarX, Trash2, Plus, AlertTriangle } from 'lucide-react';
import { getClosureImpact, cancelBookingsForClosure } from '@/services/pos-api';
// "2026-07-26" -> "Sun, Jul 26, 2026" (parsed as a plain calendar date, no TZ shift).
function formatDate(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d)
        return dateStr;
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}
// ISO timestamp -> "2:30 PM" in venue timezone.
function formatVenueTime(iso) {
    return new Date(iso).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/Halifax',
    });
}
/**
 * Admin dialog to manage venue closure dates (holidays / one-off closures).
 * Closures block online booking and drive the customer-facing announcement banner.
 */
export function MCClosedDatesDialog({ open, onOpenChange, currentDates, onSave }) {
    const [dates, setDates] = useState(currentDates);
    const [newDate, setNewDate] = useState('');
    const [newReason, setNewReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Impact preview for the date currently being entered.
    const [impact, setImpact] = useState([]);
    const [impactLoading, setImpactLoading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelResult, setCancelResult] = useState(null);
    useEffect(() => {
        if (open) {
            setDates(currentDates);
            setNewDate('');
            setNewReason('');
            setError(null);
            setSaving(false);
            setImpact([]);
            setCancelResult(null);
        }
    }, [open, currentDates]);
    // Fetch affected bookings whenever a valid date is entered.
    useEffect(() => {
        setCancelResult(null);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
            setImpact([]);
            return;
        }
        let cancelled = false;
        setImpactLoading(true);
        getClosureImpact(newDate)
            .then((bookings) => {
            if (!cancelled)
                setImpact(bookings);
        })
            .catch(() => {
            if (!cancelled)
                setImpact([]);
        })
            .finally(() => {
            if (!cancelled)
                setImpactLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [newDate]);
    const handleCancelBookings = async () => {
        setCancelling(true);
        setError(null);
        try {
            const { cancelled, emailed } = await cancelBookingsForClosure(newDate, newReason.trim() || undefined);
            setCancelResult(`Cancelled ${cancelled} booking${cancelled === 1 ? '' : 's'} · emailed ${emailed} customer${emailed === 1 ? '' : 's'}.`);
            setImpact([]);
        }
        catch (err) {
            setError(err?.message || 'Failed to cancel bookings');
        }
        finally {
            setCancelling(false);
        }
    };
    const addDate = () => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
            setError('Pick a valid date');
            return;
        }
        if (dates.some((d) => d.date === newDate)) {
            setError('That date is already in the list');
            return;
        }
        const reason = newReason.trim();
        const next = [...dates, reason ? { date: newDate, reason } : { date: newDate }].sort((a, b) => a.date.localeCompare(b.date));
        setDates(next);
        setNewDate('');
        setNewReason('');
        setError(null);
    };
    const removeDate = (date) => {
        setDates(dates.filter((d) => d.date !== date));
    };
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await onSave(dates);
            onOpenChange(false);
        }
        catch (err) {
            setError(err?.message || 'Failed to save');
        }
        finally {
            setSaving(false);
        }
    };
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Halifax' });
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { className: "mc-dialog-content sm:max-w-[460px] duration-300 data-[state=open]:slide-in-from-top-4 data-[state=closed]:slide-out-to-top-4", children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { className: "text-white flex items-center gap-2", children: [_jsx(CalendarX, { className: "h-4 w-4 text-[color:var(--mc-cyan)]" }), "Closed Dates"] }), _jsx(DialogDescription, { className: "text-[color:var(--mc-text-meta,#94a3b8)]", children: "Block online booking on holidays or one-off closures. Customers see an announcement banner." })] }), _jsxs("div", { className: "space-y-2 py-2 max-h-[240px] overflow-y-auto", children: [dates.length === 0 && (_jsx("p", { className: "mc-meta text-sm text-[color:var(--mc-text-meta,#94a3b8)]", children: "No closures scheduled." })), dates.map((d) => (_jsxs("div", { className: "flex items-center gap-2 rounded-md border border-[color:var(--mc-border,#334155)] px-3 py-2", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: "text-white text-sm font-medium", children: formatDate(d.date) }), d.reason && _jsx("div", { className: "mc-meta text-xs truncate", children: d.reason })] }), _jsx("button", { type: "button", className: "mc-btn", "aria-label": `Remove ${d.date}`, onClick: () => removeDate(d.date), children: _jsx(Trash2, { className: "h-4 w-4" }) })] }, d.date)))] }), _jsxs("div", { className: "flex flex-col gap-2 pt-2 border-t border-[color:var(--mc-border,#334155)]", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { type: "date", value: newDate, min: todayStr, onChange: (e) => setNewDate(e.target.value), className: "mc-input flex-1 mc-mono" }), _jsxs("button", { type: "button", className: "mc-btn mc-btn-primary", onClick: addDate, children: [_jsx(Plus, { className: "h-4 w-4" }), "Add"] })] }), _jsx("input", { type: "text", value: newReason, placeholder: "Reason (optional) \u2014 shown to customers", onChange: (e) => setNewReason(e.target.value), onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    addDate();
                            }, className: "mc-input w-full text-sm", maxLength: 120 })] }), impactLoading && (_jsx("p", { className: "mc-meta text-xs mc-mono text-[color:var(--mc-text-meta,#94a3b8)]", children: "Checking existing bookings\u2026" })), cancelResult && (_jsx("p", { className: "text-[color:var(--mc-green,#5fd692)] text-xs mc-mono", children: cancelResult })), !impactLoading && impact.length > 0 && (_jsxs("div", { className: "rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2", children: [_jsxs("div", { className: "flex items-start gap-2", children: [_jsx(AlertTriangle, { className: "h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" }), _jsxs("div", { className: "text-xs text-amber-100", children: [_jsxs("span", { className: "font-semibold", children: [impact.length, " existing booking", impact.length === 1 ? '' : 's', " on ", formatDate(newDate), "."] }), ' ', "Cancel ", impact.length === 1 ? 'it' : 'them', " and email the customers before closing this day."] })] }), _jsx("div", { className: "max-h-[120px] overflow-y-auto space-y-1", children: impact.map((b) => (_jsxs("div", { className: "flex items-center gap-2 text-[11px] mc-mono text-amber-100/90", children: [_jsx("span", { className: "min-w-[64px]", children: formatVenueTime(b.startTime) }), _jsxs("span", { className: "flex-1 truncate", children: [b.customerName, " \u00B7 ", b.roomName, " \u00B7 ", b.players, "p"] }), !b.hasEmail && (_jsx("span", { className: "text-amber-400/80", title: "No email on file \u2014 customer will not be notified", children: "no email" }))] }, b.id))) }), _jsx("button", { type: "button", className: "mc-btn w-full text-xs border border-amber-500/50 text-amber-200 hover:bg-amber-500/20", onClick: handleCancelBookings, disabled: cancelling, children: cancelling
                                ? 'Cancelling…'
                                : `Cancel ${impact.length} booking${impact.length === 1 ? '' : 's'} & email customers` })] })), error && (_jsx("p", { className: "text-[color:var(--mc-text-accent-pink,#ff5caa)] text-xs mc-mono", children: error })), _jsxs(DialogFooter, { className: "sm:justify-end gap-2", children: [_jsx("button", { type: "button", className: "mc-btn", onClick: () => onOpenChange(false), disabled: saving, children: "Cancel" }), _jsx("button", { type: "button", className: "mc-btn mc-btn-primary", onClick: handleSave, disabled: saving, children: saving ? 'Saving…' : 'Save' })] })] }) }));
}
